import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { UserWithResolvedScopes } from '@klareospace/pharos-lib-types';
import { createMutationCallbacks } from '@klareospace/pharos-lib-web-utils';

import { driverKeys } from '../../drivers/api';
import { tenantKeys } from '../../organization/api';
import { vehicleKeys } from '../../vehicles/api';
import { createUserApi, userKeys } from '../api';
import type { UpdateUserData } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UpdateUserVariables {
  data: UpdateUserData;
  id: string;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUpdateUser(): UseMutationResult<UserWithResolvedScopes, Error, UpdateUserVariables> {
  const client = useApiClient('admin-auth');
  const api = createUserApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation<UserWithResolvedScopes, Error, UpdateUserVariables>({
    mutationFn: ({ data, id }: UpdateUserVariables) => api.updateUser(id, data),
    ...createMutationCallbacks({
      errorKey: 'users.edit.error',
      invalidateKeys: [['current-user'], userKeys.all, tenantKeys.all, driverKeys.all, vehicleKeys.all],
      queryClient,
      successKey: 'users.edit.success',
      t,
    }),
  });
}
